<template>
  <v-card outlined class="pa-3">
    <div class="subtitle-2 mb-3">{{ $t("shift_settings") }}</div>
    <v-radio-group
      :value="applyToAllShifts"
      @change="$emit('update', $event)"
      mandatory
      hide-details
    >
      <v-radio :value="true">
        <template v-slot:label>
          <div>{{ $t("apply_to_all_shifts") }}</div>
        </template>
      </v-radio>
      <v-radio :value="false">
        <template v-slot:label>
          <div>{{ $t("different_limits_per_shift") }}</div>
        </template>
      </v-radio>
    </v-radio-group>

    <!-- Single input when "apply to all shifts" is selected -->
    <v-row v-if="applyToAllShifts" class="mt-3">
      <v-col v-if="activeType === 'voucher'" cols="12" sm="6">
        <text-field
          :value="quantityValue"
          @input="$emit('update-quantity', $event)"
          :label="$t('how_many_vouchers_per_shift')"
          type="number"
          outlined
          dense
          :hint="$t('number_of_vouchers_available_per_shift')"
          persistent-hint
        />
      </v-col>
      <v-col v-if="activeType === 'order' && limitByQuantity" cols="12" sm="6">
        <text-field
          :value="maxOrdersValue"
          @input="$emit('update-max-orders', $event)"
          :label="$t('how_many_orders_per_shift')"
          type="number"
          outlined
          dense
          :hint="$t('max_orders_hint')"
          persistent-hint
        />
      </v-col>
      <v-col v-if="activeType === 'order' && limitByValue" cols="12" sm="6">
        <text-field
          :value="maxOrderValue"
          @input="$emit('update-max-order-value', $event)"
          :label="$t('max_order_value')"
          type="number"
          suffix="KM"
          outlined
          dense
          :hint="$t('max_order_value_hint')"
          persistent-hint
        />
      </v-col>
      <v-col v-if="activeType === 'credit'" cols="12" sm="6">
        <text-field
          :value="maxAmountValue"
          @input="$emit('update-max-amount', $event)"
          :label="$t('credit_amount_per_shift')"
          type="number"
          suffix="KM"
          outlined
          dense
          :hint="$t('credit_amount_renews_per_shift')"
          persistent-hint
        />
      </v-col>
      <v-col v-if="activeType === 'money'" cols="12" sm="6">
        <text-field
          :value="limitAmountValue"
          @input="$emit('update-limit-amount', $event)"
          :label="$t('money_limit_amount_per_shift')"
          type="number"
          suffix="KM"
          outlined
          dense
          :hint="$t('money_limit_amount_per_shift_hint')"
          persistent-hint
        />
      </v-col>
    </v-row>

    <!-- Multiple inputs when "different limits per shift" is selected -->
    <div
      v-if="!applyToAllShifts && quantityHint"
      class="text-body-2 mt-6 mb-2"
    >
      {{ quantityHint }}
    </div>

    <v-row
      v-if="!applyToAllShifts"
      :class="quantityHint ? 'mt-0' : 'mt-2'"
    >
      <v-col
        v-if="shouldShowQuantityFields"
        v-for="shift in 4"
        :key="shift"
        cols="12"
        sm="6"
        md="3"
        class="py-0"
      >
        <text-field
          :label="getShiftLabel(shift)"
          :suffix="quantitySuffix"
          type="number"
          outlined
          dense
          v-model.number="shiftQuantityValues[shift - 1]"
        />
      </v-col>

      <v-col
        cols="12"
        v-if="!applyToAllShifts && shouldShowValueFields"
        class="text-body-2"
      >
        {{ $t("max_order_value_hint") }}
      </v-col>
      <v-col
        v-if="shouldShowValueFields"
        v-for="shift in 4"
        :key="'value-' + shift"
        cols="12"
        sm="6"
        md="3"
        class="pt-0"
      >
        <text-field
          :label="getShiftLabel(shift)"
          type="number"
          suffix="KM"
          outlined
          dense
          v-model.number="shiftValueLimits[shift - 1]"
        />
      </v-col>
    </v-row>
  </v-card>
</template>

<script>
export default {
  name: "ShiftSettingsCard",
  props: {
    applyToAllShifts: {
      type: Boolean,
      default: true,
    },
    shiftLimits: {
      type: Object,
      default: () => ({
        quantity_in_period: [null, null, null, null],
        max_orders: [null, null, null, null],
        max_order_value: [null, null, null, null],
        max_amount: [null, null, null, null],
        limit_amount: [null, null, null, null],
      }),
    },
    activeType: {
      type: String,
      default: null,
    },
    limitByQuantity: {
      type: Boolean,
      default: false,
    },
    limitByValue: {
      type: Boolean,
      default: false,
    },
    quantityValue: {
      type: Number,
      default: null,
    },
    maxOrdersValue: {
      type: Number,
      default: null,
    },
    maxOrderValue: {
      type: Number,
      default: null,
    },
    maxAmountValue: {
      type: Number,
      default: null,
    },
    limitAmountValue: {
      type: Number,
      default: null,
    },
  },
  computed: {
    shiftQuantityValues() {
      if (this.activeType === "voucher") return this.shiftLimits.quantity_in_period;
      if (this.activeType === "order" && this.limitByQuantity) return this.shiftLimits.max_orders;
      if (this.activeType === "credit") return this.shiftLimits.max_amount;
      if (this.activeType === "money") return this.shiftLimits.limit_amount;
      return this.shiftLimits.quantity_in_period;
    },
    shiftValueLimits() {
      return this.shiftLimits.max_order_value;
    },
    shouldShowQuantityFields() {
      return (
        this.activeType === "voucher" ||
        (this.activeType === "order" && this.limitByQuantity) ||
        this.activeType === "credit" ||
        this.activeType === "money"
      );
    },
    shouldShowValueFields() {
      return this.activeType === "order" && this.limitByValue;
    },
    quantityHint() {
      const hints = {
        voucher: this.$t("number_of_vouchers_available_per_shift"),
        order: this.$t("max_orders_hint"),
        credit: this.$t("credit_amount_renews_per_shift"),
        money: this.$t("money_limit_amount_per_shift_hint"),
      };
      return hints[this.activeType] || "";
    },
    quantitySuffix() {
      return this.activeType === "credit" || this.activeType === "money" ? "KM" : "";
    },
  },
  methods: {
    getShiftLabel(shiftNumber) {
      const labels = {
        1: this.$t("first_shift"),
        2: this.$t("second_shift"),
        3: this.$t("third_shift"),
        4: this.$t("fourth_shift"),
      };
      return labels[shiftNumber] || `${shiftNumber}. ${this.$t("shift")}`;
    },
  },
};
</script>
